import React, { useEffect, useState, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import StatCard from '../components/StatCard';
import { UsersIcon, ClockIcon, PieChartIcon, TrendingUpIcon, BuildingIcon, AlertTriangleIcon } from '../components/Icon';
import { api } from '../utils/api';

const PIE_COLORS = ['#b8862e', '#d4a84b', '#7a5a1e', '#e8c070', '#a07025', '#6b9e78'];

const SOURCE_BAR_COLORS = {
  'Digital Marketing': '#2980b9',
  'Walk In':           '#27ae60',
  'Channel Partner':   '#e67e22',
  'Reference':         '#8e44ad',
};

const TOOLTIP_STYLE = { borderRadius: 6, border: '1px solid #e0d0a8', fontSize: 12 };
const AXIS_TICK = { fontSize: 11, fill: '#888' };
const CHART_MARGIN = { top: 5, right: 10, left: -10, bottom: 5 };

function today() {
  return new Date().toISOString().split('T')[0];
}

function twelveMonthsAgo() {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 1);
  return d.toISOString().split('T')[0];
}

export default function VisitorDashboard() {
  const [from, setFrom]       = useState(twelveMonthsAgo());
  const [to, setTo]           = useState(today());
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(false);

  const fetchData = useCallback(async (f, t) => {
    setLoading(true);
    setError(false);
    try {
      const res = await api.visitorDashboard({ from: f, to: t });
      if (res?.error || !res) {
        setError(true);
        setData(null);
      } else {
        setData(res);
      }
    } catch {
      setError(true);
      setData(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(from, to); }, [from, to, fetchData]);

  const handleFromChange = (e) => setFrom(e.target.value);
  const handleToChange   = (e) => setTo(e.target.value);

  if (loading) {
    return (
      <div className="dash-loader">
        <div className="spinner spinner--lg" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="dash-error">
        <div><AlertTriangleIcon size={42} /></div>
        <div className="dash-error-title">Cannot load visitor analytics</div>
        <div className="dash-error-sub">Make sure XAMPP is running and the PHP API is placed correctly.</div>
      </div>
    );
  }

  const bySource   = (data.by_source  || []).map(r => ({ name: r.source, value: Number(r.c) }));
  const byProject  = (data.by_project || []).map(r => ({ name: r.n,      value: Number(r.c) }));
  const byMonth    = (data.by_month   || []).map(r => ({ name: r.label,  count: Number(r.c) }));
  const byWeek     = (data.by_week    || []).map(r => ({ name: r.week_start, count: Number(r.c) }));

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Site Visit Dashboard</h1>
        </div>
        <div className="dash-header-controls">
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#555' }}>
            From
            <input
              type="date"
              className="dash-dropdown"
              value={from}
              onChange={handleFromChange}
              style={{ padding: '5px 8px' }}
            />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#555' }}>
            To
            <input
              type="date"
              className="dash-dropdown"
              value={to}
              onChange={handleToChange}
              style={{ padding: '5px 8px' }}
            />
          </label>
          <div className="dash-date-chip">
            {new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </div>
        </div>
      </div>

      <div className="dash-stats-grid">
        <StatCard
          label="Total Visitors"
          value={data.total ?? 0}
          icon={<UsersIcon size={20} />}
          color="#b8862e"
          sub="In selected date range"
        />
        <StatCard
          label="Today's Visitors"
          value={data.today ?? 0}
          icon={<ClockIcon size={20} />}
          color="#27ae60"
          sub="Walked in today"
        />
      </div>

      <div className="dash-charts-row">
        <div className="dash-chart-card">
          <div className="dash-chart-title"><PieChartIcon size={15} /> <span>By Source</span></div>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie
                data={bySource}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={85}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                labelLine={false}
                fontSize={10}
              >
                {bySource.map((entry, i) => (
                  <Cell key={i} fill={SOURCE_BAR_COLORS[entry.name] || PIE_COLORS[i % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Legend iconType="circle" iconSize={10} wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="dash-chart-card">
          <div className="dash-chart-title"><BuildingIcon size={15} /> <span>By Project</span></div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={byProject} margin={CHART_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0e8d4" />
              <XAxis dataKey="name" tick={AXIS_TICK} />
              <YAxis tick={AXIS_TICK} allowDecimals={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Bar dataKey="value" fill="#b8862e" radius={[4, 4, 0, 0]} name="Visitors">
                {byProject.map((_, i) => (
                  <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="dash-charts-row">
        <div className="dash-chart-card" style={{ flex: 2 }}>
          <div className="dash-chart-title"><TrendingUpIcon size={15} /> <span>By Month (last 12 months)</span></div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={byMonth} margin={CHART_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0e8d4" />
              <XAxis dataKey="name" tick={AXIS_TICK} />
              <YAxis tick={AXIS_TICK} allowDecimals={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Bar dataKey="count" fill="#b8862e" radius={[4, 4, 0, 0]} name="Visitors" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="dash-chart-card" style={{ flex: 1 }}>
          <div className="dash-chart-title"><TrendingUpIcon size={15} /> <span>By Week (last 8 weeks)</span></div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={byWeek} margin={CHART_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0e8d4" />
              <XAxis dataKey="name" tick={AXIS_TICK} />
              <YAxis tick={AXIS_TICK} allowDecimals={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Bar dataKey="count" fill="#d4a84b" radius={[4, 4, 0, 0]} name="Visitors" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
